import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';

const integrations = [
  { name: 'EEX', label: 'European Energy Exchange', icon: 'ri-stock-line', color: '#1F5B8F' },
  { name: 'Grexel', label: 'GO Registry & Platform', icon: 'ri-leaf-line', color: '#0D7A3C' },
  { name: 'FlexiDAO', label: 'Blockchain EAC Tracking', icon: 'ri-links-line', color: '#B8860B' },
  { name: 'Ukrenergo', label: 'НЕК Укренерго API', icon: 'ri-plug-line', color: '#D12229' },
];

const features = [
  { icon: 'ri-award-line', title: 'GO Сертифікати', desc: 'Автоматична генерація та реєстрація сертифікатів походження в Grexel Registry' },
  { icon: 'ri-global-line', title: 'CBAM-ready', desc: 'Підтримка вимог Carbon Border Adjustment Mechanism для EU-ринку' },
  { icon: 'ri-exchange-line', title: 'EEX Trading', desc: 'Прямий доступ до торгів GO сертифікатами на European Energy Exchange' },
  { icon: 'ri-shield-check-line', title: 'Blockchain верифікація', desc: 'Незмінний аудит через FlexiDAO для корпоративних покупців' },
  { icon: 'ri-file-chart-line', title: 'Автозвітність', desc: 'Автоматичне формування звітів для регулятора НКРЕКП та НЕК' },
  { icon: 'ri-team-line', title: 'PPA Integration', desc: 'Прив\'язка GO до PPA-контрактів з корпоративними покупцями' },
];

const timeline = [
  { day: 'День 1–3', title: 'Підключення та верифікація', desc: 'Верифікація станції, підключення до Grexel Registry' },
  { day: 'День 4–7', title: 'Первинна емісія GO', desc: 'Перша партія сертифікатів автоматично зареєстрована' },
  { day: 'День 8–14', title: 'Торги та оптимізація', desc: 'Перші продажі на EEX, оптимізація стратегії ціноутворення' },
];

export default function ModuleDetailPage() {
  const navigate = useNavigate();
  const [trialStarted, setTrialStarted] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'roi' | 'integrations' | 'faq'>('overview');

  const handleStartTrial = () => {
    setTrialStarted(true);
  };

  return (
    <DashboardLayout breadcrumb={['Ринок', 'GO', 'HeliosGO']}>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/')}
            className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer mt-1 transition-colors"
            style={{ background: '#fff', border: '1px solid #E5E5E8', color: '#6B6B70' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#F5F5F7'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            <i className="ri-arrow-left-line text-sm"></i>
          </button>
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider" style={{ background: '#D12229', color: '#fff' }}>HOT</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider" style={{ background: 'rgba(184,134,11,0.15)', color: '#B8860B', border: '1px solid rgba(184,134,11,0.3)' }}>TIER A</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider" style={{ background: 'rgba(31,91,143,0.1)', color: '#1F5B8F', border: '1px solid rgba(31,91,143,0.2)' }}>NEW</span>
            </div>
            <h1
              className="text-2xl font-bold"
              style={{ color: '#1A1A1A', letterSpacing: '-0.02em', fontFamily: 'Inter, sans-serif' }}
            >
              HeliosGO
            </h1>
            <p className="text-sm mt-1" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>
              Сертифікати походження (GO) · CBAM-ready · Marketplace доступ до EEX
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-xs" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>Від</div>
            <div className="text-xl font-bold tabular-nums" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>€90<span className="text-sm font-normal text-gray-500">/міс</span></div>
          </div>
          {trialStarted ? (
            <div
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: 'rgba(13,122,60,0.1)', color: '#0D7A3C', border: '1px solid rgba(13,122,60,0.2)', fontFamily: 'Inter, sans-serif' }}
            >
              <i className="ri-checkbox-circle-line"></i> Пробний запущено!
            </div>
          ) : (
            <button
              onClick={handleStartTrial}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
              style={{ background: '#D12229', color: '#fff', fontFamily: 'Inter, sans-serif' }}
            >
              Спробувати 14 днів безкоштовно
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex items-center gap-0 mb-6 rounded-xl p-1 w-fit"
        style={{ background: '#fff', border: '1px solid #E5E5E8' }}
      >
        {([
          { id: 'overview', label: 'Огляд' },
          { id: 'roi', label: 'ROI Калькулятор' },
          { id: 'integrations', label: 'Інтеграції' },
          { id: 'faq', label: 'FAQ' },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
            style={{
              background: activeTab === tab.id ? '#D12229' : 'transparent',
              color: activeTab === tab.id ? '#fff' : '#6B6B70',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="xl:col-span-2 flex flex-col gap-6">
          {activeTab === 'overview' && (
            <>
              {/* Description */}
              <div
                className="rounded-xl p-6"
                style={{ background: '#fff', border: '1px solid #E5E5E8' }}
              >
                <h2 className="text-base font-semibold mb-3" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                  Про модуль
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>
                  <strong style={{ color: '#1A1A1A' }}>HeliosGO</strong> — це повністю автоматизована платформа для управління сертифікатами походження (Guarantees of Origin) відповідно до директив EU 2018/2001 та CBAM. Модуль інтегрується з реєстрами Grexel, торговою платформою EEX та API НЕК Укренерго для автоматичної емісії, верифікації та продажу GO сертифікатів.
                </p>
                <p className="text-sm leading-relaxed mt-3" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>
                  Завдяки blockchain-верифікації через <strong style={{ color: '#1A1A1A' }}>FlexiDAO</strong>, кожен МВт·г вашої чистої енергії отримує незмінний цифровий паспорт, що підвищує довіру корпоративних покупців та дозволяє продавати GO за преміальними цінами.
                </p>
              </div>

              {/* Features */}
              <div
                className="rounded-xl p-6"
                style={{ background: '#fff', border: '1px solid #E5E5E8' }}
              >
                <h2 className="text-base font-semibold mb-4" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                  Функціональність
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {features.map((f) => (
                    <div
                      key={f.title}
                      className="flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-gray-50"
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: 'rgba(209,34,41,0.08)' }}
                      >
                        <i className={`${f.icon} text-sm`} style={{ color: '#D12229' }}></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>{f.title}</div>
                        <div className="text-xs mt-0.5 leading-relaxed" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{f.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div
                className="rounded-xl p-6"
                style={{ background: '#fff', border: '1px solid #E5E5E8' }}
              >
                <h2 className="text-base font-semibold mb-4" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                  Запуск за 14 днів
                </h2>
                <div className="relative">
                  <div
                    className="absolute left-4 top-4 bottom-4 w-0.5"
                    style={{ background: '#E5E5E8' }}
                  ></div>
                  <div className="space-y-4">
                    {timeline.map((item, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative z-10"
                          style={{ background: '#D12229', color: '#fff', fontSize: '11px', fontWeight: 700, fontFamily: 'Inter, sans-serif' }}
                        >
                          {i + 1}
                        </div>
                        <div className="pt-1">
                          <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#D12229', fontFamily: 'Inter, sans-serif' }}>
                            {item.day}
                          </div>
                          <div className="text-sm font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>{item.title}</div>
                          <div className="text-xs mt-0.5" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'roi' && (
            <div
              className="rounded-xl p-6"
              style={{ background: '#fff', border: '1px solid #E5E5E8' }}
            >
              <h2 className="text-base font-semibold mb-4" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                ROI Аналіз для SES Tabaki, 5 МВт
              </h2>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  { label: 'Витрати (модуль/міс)', value: '€90', sub: 'Базовий план', positive: false },
                  { label: 'GO Premium Setup', value: '€3,640', sub: 'Одноразово', positive: false },
                  { label: 'Очікуваний дохід/міс', value: '€8,400', sub: '+125% ROI', positive: true },
                  { label: 'Окупність', value: '< 1 міс', sub: 'Від першої емісії', positive: true },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl p-4"
                    style={{
                      background: item.positive ? 'rgba(13,122,60,0.05)' : 'rgba(209,34,41,0.04)',
                      border: `1px solid ${item.positive ? 'rgba(13,122,60,0.15)' : 'rgba(209,34,41,0.1)'}`,
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{item.label}</div>
                    <div
                      className="text-xl font-bold tabular-nums"
                      style={{ color: item.positive ? '#0D7A3C' : '#D12229', fontFamily: 'Inter, sans-serif' }}
                    >
                      {item.value}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{item.sub}</div>
                  </div>
                ))}
              </div>

              {/* Detailed breakdown */}
              <div style={{ borderTop: '1px solid #E5E5E8' }} className="pt-4">
                <div className="text-xs font-semibold mb-3" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Деталізація доходу</div>
                <div className="space-y-2">
                  {[
                    { item: 'Обсяг виробітку/міс', value: '600 МВт·г' },
                    { item: 'GO сертифікатів/міс', value: '600 шт.' },
                    { item: 'Середня ціна GO (EEX)', value: '€14/МВт·г' },
                    { item: 'Валовий дохід GO', value: '€8,400' },
                    { item: 'Вартість модуля', value: '-€90' },
                    { item: 'Чистий дохід GO', value: '€8,310' },
                  ].map((row) => (
                    <div key={row.item} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #F5F5F7' }}>
                      <span className="text-xs" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{row.item}</span>
                      <span
                        className="text-xs font-semibold tabular-nums"
                        style={{
                          color: row.item.includes('Чистий') ? '#0D7A3C' : row.item.includes('Вартість') ? '#D12229' : '#1A1A1A',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div
              className="rounded-xl p-6"
              style={{ background: '#fff', border: '1px solid #E5E5E8' }}
            >
              <h2 className="text-base font-semibold mb-4" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                Підключені інтеграції
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {integrations.map((intg) => (
                  <div
                    key={intg.name}
                    className="flex items-start gap-4 p-4 rounded-xl transition-colors hover:bg-gray-50"
                    style={{ border: '1px solid #E5E5E8' }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `${intg.color}15` }}
                    >
                      <i className={`${intg.icon} text-lg`} style={{ color: intg.color }}></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>{intg.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{intg.label}</div>
                      <div className="flex items-center gap-1.5 mt-2">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#0D7A3C' }}></div>
                        <span className="text-[10px] font-medium" style={{ color: '#0D7A3C' }}>Підключено</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'faq' && (
            <div
              className="rounded-xl overflow-hidden"
              style={{ background: '#fff', border: '1px solid #E5E5E8' }}
            >
              <div className="px-6 py-4" style={{ borderBottom: '1px solid #E5E5E8' }}>
                <h2 className="text-base font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                  Часті запитання
                </h2>
              </div>
              {[
                { q: 'Що таке GO сертифікати?', a: 'Guarantees of Origin — це цифрові сертифікати, що підтверджують виробництво 1 МВт·г електроенергії з відновлюваних джерел. Вони торгуються окремо від електроенергії на ринках ЄС та Украіни.' },
                { q: 'Чи потрібно мати ліцензію для торгівлі GO?', a: 'HeliosGO включає в себе підтримку з отриманням необхідних акредитацій. Наша команда допомагає з реєстрацією в реєстрі НКРЕКП та Grexel.' },
                { q: 'Яка мінімальна кількість МВт для участі?', a: 'Мінімум 1 МВт встановленої потужності. Для SES Tabaki (5 МВт) щомісяця генерується ~600 сертифікатів.' },
                { q: 'Чи сумісний модуль з CBAM?', a: 'Так. HeliosGO повністю відповідає вимогам Carbon Border Adjustment Mechanism, що дозволяє продавати GO корпоративним покупцям у ЄС без додаткових витрат на відповідність.' },
                { q: 'Скільки часу займає запуск?', a: 'Повний запуск займає 7–14 робочих днів: верифікація станції, підключення до реєстрів та перша емісія сертифікатів.' },
              ].map((faq, i) => (
                <div
                  key={i}
                  className="px-6 py-4"
                  style={{ borderBottom: i < 4 ? '1px solid #F5F5F7' : 'none' }}
                >
                  <div className="text-sm font-semibold mb-1.5" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
                    {faq.q}
                  </div>
                  <div className="text-sm leading-relaxed" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>
                    {faq.a}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="flex flex-col gap-4">
          {/* CTA card */}
          <div
            className="rounded-xl p-5 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1A0A0B 0%, #2D0A0D 100%)',
              border: '1px solid rgba(209,34,41,0.2)',
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse 80% 60% at 80% 20%, rgba(209,34,41,0.2) 0%, transparent 60%)' }}
            ></div>
            <div className="relative z-10">
              <div className="text-sm font-bold text-white mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>14-денний пробний доступ</div>
              <div className="text-xs mb-4" style={{ color: '#8B8B94', fontFamily: 'Inter, sans-serif' }}>Без прив&apos;язки картки · Скасування будь-коли</div>
              {trialStarted ? (
                <div
                  className="flex items-center gap-2 justify-center py-2.5 rounded-xl text-sm font-semibold"
                  style={{ background: 'rgba(13,122,60,0.2)', color: '#0D7A3C', border: '1px solid rgba(13,122,60,0.3)', fontFamily: 'Inter, sans-serif' }}
                >
                  <i className="ri-checkbox-circle-line"></i> Активовано!
                </div>
              ) : (
                <button
                  onClick={handleStartTrial}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
                  style={{ background: '#D12229', color: '#fff', fontFamily: 'Inter, sans-serif' }}
                >
                  Почати пробний →
                </button>
              )}
            </div>
          </div>

          {/* Quick ROI */}
          <div
            className="rounded-xl p-5"
            style={{ background: '#fff', border: '1px solid #E5E5E8' }}
          >
            <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>Швидкий ROI</div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>Витрати/міс</span>
                <span className="text-sm font-bold tabular-nums" style={{ color: '#D12229', fontFamily: 'Inter, sans-serif' }}>€3,730</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>Дохід GO/міс</span>
                <span className="text-sm font-bold tabular-nums" style={{ color: '#0D7A3C', fontFamily: 'Inter, sans-serif' }}>€8,400</span>
              </div>
              <div style={{ borderTop: '1px solid #E5E5E8' }} className="pt-3 flex justify-between items-center">
                <span className="text-xs font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>Прибуток/міс</span>
                <span className="text-base font-bold tabular-nums" style={{ color: '#0D7A3C', fontFamily: 'Inter, sans-serif' }}>+€4,670</span>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div
            className="rounded-xl p-5"
            style={{ background: '#fff', border: '1px solid #E5E5E8' }}
          >
            <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>Тарифи</div>
            {[
              { name: 'Starter', price: '€90', desc: 'до 5 МВт, Grexel Registry' },
              { name: 'Business', price: '€210', desc: 'до 20 МВт, EEX + FlexiDAO', recommended: true },
              { name: 'Enterprise', price: 'Запит', desc: 'Без обмежень, SLA + API' },
            ].map((plan) => (
              <div
                key={plan.name}
                className="flex items-start gap-3 p-3 rounded-lg mb-2 last:mb-0"
                style={{
                  background: plan.recommended ? 'rgba(209,34,41,0.04)' : 'transparent',
                  border: plan.recommended ? '1px solid rgba(209,34,41,0.15)' : '1px solid transparent',
                }}
              >
                {plan.recommended && (
                  <i className="ri-star-fill text-sm shrink-0 mt-0.5" style={{ color: '#D12229' }}></i>
                )}
                {!plan.recommended && <div className="w-4 shrink-0"></div>}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>{plan.name}</span>
                    {plan.recommended && <span className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#D12229', color: '#fff' }}>РЕКОМЕНДОВАНО</span>}
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>{plan.desc}</div>
                </div>
                <span className="text-sm font-bold tabular-nums shrink-0" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>{plan.price}</span>
              </div>
            ))}
          </div>

          {/* Support */}
          <div
            className="rounded-xl p-4"
            style={{ background: '#F9F9FB', border: '1px solid #E5E5E8' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#1A1A1A' }}>
                <i className="ri-customer-service-2-line text-sm text-white"></i>
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>Менеджер модуля</div>
                <div className="text-[11px]" style={{ color: '#6B6B70', fontFamily: 'Inter, sans-serif' }}>Ірина К. · go@helios.energy</div>
              </div>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors"
                style={{ background: '#fff', border: '1px solid #E5E5E8' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F5F5F7'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                <i className="ri-mail-line text-sm" style={{ color: '#1A1A1A' }}></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
